(() => {
  'use strict';

  if (customElements.get('product-badge-block')) return;

  /** What a variant slot is showing right now, as a value two slots can be compared by. */
  const visibleBadgeSignature = (fragment) => {
    if (!fragment) return '';

    return [...fragment.querySelectorAll('product-badge-group')]
      .map((group) => {
        if (group.hidden) return '';

        const badge = group.querySelector(
          '[data-product-badge]:not([hidden]), [data-product-badge-fallback]:not([hidden])'
        );
        if (!badge) return '';

        const sources = [...badge.querySelectorAll('img')].map(
          (image) => image.getAttribute('src') || ''
        );
        return [badge.textContent.replace(/\s+/g, ' ').trim(), ...sources].join('~');
      })
      .join('|');
  };

  class ProductBadgeBlock extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.variantFragments = [...this.querySelectorAll('[data-product-badge-variant]')];
      this.emptyState = this.querySelector('[data-product-badge-empty]');
      this.swapHost = this.querySelector('[data-product-badge-swap]');

      document.addEventListener(
        'dieux:product-variant-change',
        (event) => this.handleVariantChange(event),
        { signal: this.abortController.signal }
      );

      this.addEventListener('dieux:product-badge-resolved', () => this.syncVisibility(), {
        signal: this.abortController.signal,
      });

      this.syncVisibility();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    handleVariantChange(event) {
      if (String(event.detail?.productId || '') !== String(this.dataset.productId || '')) return;
      this.selectVariant(event.detail?.variant?.id);
    }

    selectVariant(variantId) {
      const nextVariantId = String(variantId || '');
      const nextFragment = this.variantFragments.find(
        (fragment) => fragment.dataset.productBadgeVariant === nextVariantId
      );
      const currentFragment = this.variantFragments.find((fragment) => !fragment.hidden);

      if (nextFragment) {
        for (const group of nextFragment.querySelectorAll('product-badge-group')) {
          group.refresh?.();
        }
      }

      const isSilentSwap =
        Boolean(currentFragment) &&
        Boolean(nextFragment) &&
        currentFragment !== nextFragment &&
        visibleBadgeSignature(currentFragment) === visibleBadgeSignature(nextFragment);

      if (isSilentSwap) this.swapHost?.setAttribute('data-swap-instant', '');

      for (const fragment of this.variantFragments) {
        const isActive = fragment === nextFragment;
        fragment.hidden = !isActive;
        fragment.inert = !isActive;
      }

      this.dataset.activeVariantId = nextVariantId;

      if (isSilentSwap && this.swapHost) {
        void this.swapHost.offsetHeight;
        this.swapHost.removeAttribute('data-swap-instant');
      }

      this.syncVisibility();
    }

    /** Shows the container only while the active variant's slot is actually showing a badge. */
    syncVisibility() {
      const activeFragment = this.variantFragments.find((fragment) => !fragment.hidden);
      const groups = activeFragment
        ? [...activeFragment.querySelectorAll('product-badge-group')]
        : [];
      const hasContent = groups.some((group) => !group.hidden);
      const isDesignMode = this.dataset.designMode === 'true';

      this.dataset.hasActiveContent = String(hasContent);
      this.hidden = !hasContent && !isDesignMode;
      if (this.emptyState) this.emptyState.hidden = hasContent;
    }
  }

  customElements.define('product-badge-block', ProductBadgeBlock);
})();
